import {
  MultiValueContainer
} from "./chunk-2YQSQVVN.mjs";
import {
  MultiValueLabel
} from "./chunk-3AUZDRCF.mjs";
import {
  MultiValueRemove
} from "./chunk-FGZMESLW.mjs";
import {
  Option
} from "./chunk-UPBEIR7E.mjs";
import {
  SelectContainer
} from "./chunk-NGIPE3MK.mjs";
import {
  ValueContainer
} from "./chunk-AA2IRD3T.mjs";
import {
  MultiValue
} from "./chunk-Q4Z3QUO7.mjs";
import {
  TranslationsContextPovider
} from "./chunk-SKR5AE4Z.mjs";
import {
  Control
} from "./chunk-KMIUMPZG.mjs";
import {
  DropdownIndicator
} from "./chunk-Q4PGHPYR.mjs";

// src/multiselect/src/multiselect.tsx
import ReactSelect, { NonceProvider } from "react-select";
import { forwardRef, useCallback, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
function distinct(values, comparitor) {
  const map = new Map(
    values.map((value) => [comparitor(value), value])
  );
  return [...map.values()];
}
function toBaseOption({
  label,
  value
}) {
  return { label, value };
}
function formatChangeValue(values) {
  return distinct(values.map(toBaseOption), (value) => value.value);
}
function MultiselectCmp({
  id,
  cspNonce,
  formatAriaRemoveLabel,
  onChange: propsOnChange,
  options = [],
  value = [],
  ...props
}, ref) {
  const onChange = useCallback(
    (newValue) => {
      propsOnChange?.(formatChangeValue(newValue));
    },
    [propsOnChange]
  );
  const flattenedOptions = useMemo(
    () => options.reduce((collection, option) => {
      collection.push({ label: option.label, value: option.value });
      if (option.options) {
        collection.push(
          ...option.options.map((childOption) => ({
            isChild: true,
            label: childOption.label,
            parentValue: option.value,
            value: childOption.value
          }))
        );
      }
      return collection;
    }, []),
    [options]
  );
  const mappedValue = useMemo(() => {
    return value.map(
      (incomingValue) => flattenedOptions.find(
        (option) => option.value === incomingValue.value
      )
    ).filter(Boolean);
  }, [flattenedOptions, value]);
  const onInternalChange = useCallback(
    (newValue, actionMeta) => {
      const parent = flattenedOptions.find(
        (option) => option.value === actionMeta.option?.value && !Boolean(option.isChild)
      );
      const allChildrenOfParent = parent ? flattenedOptions.filter(
        (option) => option.parentValue === parent.value
      ) : [];
      const child = flattenedOptions.find(
        (option) => option.value === actionMeta.option?.value && option.isChild
      );
      if (parent && actionMeta.action === "select-option") {
        onChange([...newValue, ...allChildrenOfParent]);
        return;
      }
      if (parent && actionMeta.action === "deselect-option") {
        const filteredNew = newValue.filter(
          (option) => !Boolean(allChildrenOfParent.includes(option))
        );
        onChange(filteredNew);
        return;
      }
      if (child && actionMeta.action === "deselect-option") {
        const filteredNew = newValue.filter(
          (option) => option.value !== child.parentValue
        );
        onChange(filteredNew);
        return;
      }
      if (actionMeta.action === "remove-value" || actionMeta.action === "pop-value") {
        const optionToRemove = actionMeta.removedValue;
        const parentValue = Boolean(optionToRemove.isChild) ? optionToRemove.parentValue : optionToRemove.value;
        const filteredNew = newValue.filter(
          (option) => option.value !== parentValue && option.parentValue !== parentValue
        );
        onChange(filteredNew);
        return;
      }
      if (child && actionMeta.action === "select-option") {
        const thisParent = flattenedOptions.find(
          (option) => option.value === child.parentValue
        );
        const allChildrenOfThisParent = thisParent ? flattenedOptions.filter(
          (option) => option.parentValue === thisParent.value
        ) : [];
        const allChildrenSelected = Boolean(
          thisParent && Boolean(
            allChildrenOfThisParent.every(
              (option) => newValue.some((c) => c.value === option.value)
            )
          )
        );
        if (thisParent && allChildrenSelected) {
          onChange([...newValue, thisParent]);
          return;
        }
      }
      onChange(newValue);
    },
    [flattenedOptions, onChange]
  );
  const onBlurWorkaround = useCallback(
    (event) => {
      const element = event.relatedTarget;
      if (element && ["A", "BUTTON", "INPUT"].includes(element.tagName)) {
        element.focus();
      }
    },
    []
  );
  return /* @__PURE__ */ jsx(TranslationsContextPovider, { formatAriaRemoveLabel, children: /* @__PURE__ */ jsx(NonceProvider, { cacheKey: "gds-multiselect", nonce: cspNonce ?? "", children: /* @__PURE__ */ jsx(
    ReactSelect,
    {
      classNames: {
        control: () => "!min-h-[2rem]",
        input: () => "px-2",
        menu: () => "mt-1 rounded border-surface-strong bg-surface-light shadow-[0_4px_12px_0px_rgba(85,85,90,0.25)] whitespace-nowrap !w-auto min-w-full",
        noOptionsMessage: () => "p-4",
        option: () => "!cursor-pointer",
        placeholder: () => "px-2"
      },
      components: {
        Control,
        DropdownIndicator,
        MultiValue,
        MultiValueContainer,
        MultiValueLabel,
        MultiValueRemove,
        Option,
        SelectContainer,
        ValueContainer
      },
      hideSelectedOptions: false,
      inputId: id,
      isClearable: false,
      isMulti: true,
      options: flattenedOptions,
      ref,
      unstyled: true,
      value: mappedValue,
      onBlur: onBlurWorkaround,
      onChange: onInternalChange,
      ...props
    }
  ) }) });
}
var Multiselect = forwardRef(MultiselectCmp);

export {
  Multiselect
};
